import PropTypes from 'prop-types';
import React from 'react';

import { useTranslation, withTranslation, Trans } from 'react-i18next';
import OrganizationInfos from '../models/organizationInfos';
import DropdownPageSize from './manager/components/dropdownPageSize';
import EventCard from '../pages/events/list/eventCard';
import LoadingCard from '../components/loadingCard';
import { fetcher } from '@weezevent/weezjs-core';
import { useAuth } from 'react-oidc-context';
import {
    CheckboxLabel,
    Button,
    FilterSetController,
    Filter,
    Paginator,
    SelectHierarchy,
    Icon,
    WeezHeader,
    EmptyState,
    Card,
    ContentCard,
    Message,
    EmptyCard,
    SortButton,
    ToastContext
} from '@weezevent/nacre';
import ActionModals from '../pages/events/list/actionModals/actionModals';
import EventModel from '../models/eventModel';
import Tag from '../models/tag';
import { useHistory } from 'react-router-dom';
import { usePermissions } from '../user/permissions';
import { useSegment, useTrackingPage } from '../components/tracking/trackingProvider';
import WarningMessage from '../components/message';
import css from '../styles/event/eventFilters.module.css';
import OnboardingEventList from './events/list/components/onboardingEventList';

import FeedBacksModal from './events/list/actionModals/FeedBacksModal';
import useAppContext from '../../src/context/useAppContext';
import { useFilters, handleSearchQuery } from './manager/components/context';

const spec = {
    multiple: ['publication_status__in', 'tag__in', 'time_status'],
    fields: ['publication_status__in', 'tag__in', 'time_status', 'search', 'ordering'],
    form: [],
    aliases: {},
    date: []
};

const initialFilters = {
    publication_status__in: new Set(['draft', 'published']),
    time_status: new Set(['upcoming', 'ongoing', 'over']),
    ordering: '-is_member,start_date'
};

/**
 * Event list selection page
 * @param {*} param0
 */
export function EventSelection() {
    const { t } = useTranslation();
    const { track } = useSegment();

    const [{ organization }] = useAppContext();

    const [organizationInfos] = OrganizationInfos.useApiModel({
        organizationId: organization.id
    });

    let [currentPage, setCurrentPage] = React.useState(0);

    let [paginationEvents, setPaginationEvents] = React.useState({
        limit: 50, // Default pagination value
        offset: 0 // Default page items value
    });
    const permissions = usePermissions();
    const history = useHistory();
    const { user } = useAuth();
    const toast = React.useContext(ToastContext);
    const [filters, setFilters] = useFilters(spec, initialFilters);
    const [timeStatusFilters, setTimeStatusFilters] = React.useState({
        time_status: filters?.time_status ? new Set(filters.time_status) : new Set(['upcoming', 'ongoing', 'over'])
    });

    const [tags] = Tag.useApiModel({
        organizationId: organization.id
    });

    const [actionModalDisplay, setActionModalDisplay] = React.useState(null);
    const [eventSelected, setEventSelected] = React.useState(null);
    const [openFeedbacksModal, setOpenFeedbacksModal] = React.useState(false);
    const [width, setWidth] = React.useState(window.innerWidth);
    const [eventIdWithoutStats, setEventIdWithoutStats] = React.useState(new Set());
    const breakpointMobile = 600;
    const breakpointTablet = 960;

    const viewType = React.useMemo(() => {
        if (width <= breakpointMobile) {
            return 'mobile';
        }
        if (width >= breakpointTablet) {
            return 'desktop';
        }
        return 'tablet';
    }, [width]);

    const options = [
        { key: 'published', text: t('ticket.event.list.statusPublished'), name: 'published' },
        { key: 'draft', text: t('ticket.event.list.statusDraft'), name: 'draft' },
        { key: 'closed', text: t('ticket.event.list.statusClosed'), name: 'closed' }
    ];

    const tagOptions = React.useMemo(() => {
        let list = [];
        if (tags) {
            tags.forEach(tag => {
                list.push({ key: tag.id.toString(), text: tag.label, name: tag.id.toString() });
            });
        }
        return list;
    }, [tags]);

    const sortOptions = React.useMemo(() => {
        const options = [
            { key: 'start_date', label: t('ticket.common.sort.date-up'), value: 'start_date' },
            { key: '-start_date', label: t('ticket.common.sort.date-down'), value: '-start_date' },
            { key: 'created', label: t('ticket.common.sort.creation'), value: 'created' },
            { key: 'name', label: t('ticket.common.sort.alphabetical'), value: 'name' }
        ];
        return options;
    }, []);

    const filterOptions = React.useMemo(() => {
        const options = [
            { text: t('ticket.common.quickFilters.upcoming'), value: 'upcoming' },
            { text: t('ticket.common.quickFilters.ongoing'), value: 'ongoing' },
            { text: t('ticket.common.quickFilters.over'), value: 'over' }
        ];
        return options;
    }, []);

    /**
     * Return URL according to search query, filters, limit and offset
     */
    const queryParams = React.useCallback(() => {
        let param_object = {};

        param_object = handleSearchQuery(spec, filters, true);
        param_object['limit'] = paginationEvents.limit;
        param_object['offset'] = paginationEvents.offset;
        return param_object;
    }, [filters, paginationEvents]);

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            query: queryParams(),
            onSuccess: data => {
                const eventIdWithoutStatsTemp = data.filter(event => event.attendees === null).map(event => event.id);
                setEventIdWithoutStats(new Set([...eventIdWithoutStatsTemp, ...eventIdWithoutStats]));
            }
        },
        [organization, queryParams]
    );

    React.useEffect(() => {
        if (!eventIdWithoutStats.size) return;
        const payload = {
            type: 'async_compute_stats',
            resource_query_filter: {
                id__in: [...eventIdWithoutStats].join(',')
            },
            config: {}
        };
        fetcher.post(`/ticket/organizations/${organization.id}/events/actions`, { data: payload });
    }, [eventIdWithoutStats.size]);

    const countEvents = Number.parseInt(events?.__meta?.headers.get('total-count')) || 0;

    // Redirect to event creation if organization has no events at all
    React.useEffect(() => {
        if (!loadingEvents && organizationInfos && !organizationInfos.has_event) {
            history.push(`/ticket/O${organization.id}/events/new`);
        }
    }, [loadingEvents, organizationInfos, organization, history]);

    /**
     * Reset pagination and events ( and count event ) in order to clean everything
     */
    let resetPagination = React.useCallback(() => {
        setPaginationEvents({
            limit: paginationEvents.limit,
            offset: 0
        });
        setCurrentPage(0);
    }, [paginationEvents]);

    let handleSearch = React.useCallback(
        (synth, value) => {
            const filterSpread = { ...filters };
            if (value) {
                filterSpread['search'] = value;
            } else {
                delete filterSpread['search'];
            }
            setFilters(filterSpread);
            resetPagination();
        },
        [resetPagination, filters]
    );

    const handlePaginate = React.useCallback(value => {
        setPaginationEvents({
            limit: value,
            offset: 0
        });
    }, []);

    const handleSort = React.useCallback(
        value => {
            setFilters({ ...filters, ordering: '-is_member,' + value });
            resetPagination();
        },
        [resetPagination, filters]
    );

    const onCloseActionModal = React.useCallback(() => {
        setActionModalDisplay(null);
        setEventSelected(null);
    }, []);

    const updateTimeStatusFilters = React.useCallback(
        (name, value) => {
            setTimeStatusFilters(timeStatusFilters => {
                let previousFilters = { ...timeStatusFilters };
                if (!value) {
                    delete previousFilters[name];
                    resetPagination();
                    delete filters['time_status'];
                    setFilters({ ...filters });
                    return previousFilters;
                }
                let newFilters = {
                    ...previousFilters,
                    [name]: value
                };
                resetPagination();
                setFilters({ ...filters, ...newFilters });
                return newFilters;
            });
        },
        [resetPagination, filters]
    );

    // Onchange time status filter desktop view
    const handleTimeStatusToggle = React.useCallback(
        (value, checked) => {
            let checks = timeStatusFilters.time_status || new Set([]);

            if (checked) {
                checks.add(value);
            } else {
                checks.delete(value);
            }
            updateTimeStatusFilters('time_status', checks.size === 0 ? null : checks);
        },
        [timeStatusFilters, updateTimeStatusFilters]
    );

    // Onchange time status filter mobile view
    const handleTimeStatusHierarchy = React.useCallback(
        selected => {
            updateTimeStatusFilters('time_status', !selected.length ? null : new Set([selected]));
        },
        [updateTimeStatusFilters]
    );

    const selectedTimeStatusFilters = React.useMemo(() => {
        return timeStatusFilters.time_status ? Array.from(timeStatusFilters.time_status) : new Set([]);
    }, [timeStatusFilters]);

    const renderFilters = React.useMemo(() => {
        if (viewType === 'mobile') {
            return (
                <div className={css['filters-hierarchy']}>
                    <SelectHierarchy
                        searchable={false}
                        multi
                        height={'mini'}
                        placeholder={t('ticket.common.action.display')}
                        options={filterOptions}
                        selected={selectedTimeStatusFilters}
                        onChange={handleTimeStatusHierarchy}
                        totalCount={filterOptions.length}
                        labelSelectAll={t('ticket.common.action.select-all')}
                        labelApply={t('ticket.common.action.apply')}
                    />
                </div>
            );
        }
        return (
            <div className={css['filter-checkboxes']}>
                <span className={css['filter-boxes-label']}>{`${t('ticket.common.action.display')}: `}</span>
                <CheckboxLabel
                    label={t('ticket.common.quickFilters.upcoming')}
                    checked={timeStatusFilters.time_status?.has('upcoming')}
                    onChange={(e, st) => {
                        handleTimeStatusToggle('upcoming', st.checked);
                    }}
                />
                <CheckboxLabel
                    label={t('ticket.common.quickFilters.ongoing')}
                    checked={timeStatusFilters.time_status?.has('ongoing')}
                    onChange={(e, st) => {
                        handleTimeStatusToggle('ongoing', st.checked);
                    }}
                />
                <CheckboxLabel
                    label={t('ticket.common.quickFilters.over')}
                    checked={timeStatusFilters.time_status?.has('over')}
                    onChange={(e, st) => {
                        handleTimeStatusToggle('over', st.checked);
                    }}
                />
            </div>
        );
    }, [handleTimeStatusToggle, viewType, filterOptions, selectedTimeStatusFilters]);

    const handlePage = React.useCallback(
        value => {
            setCurrentPage(value);

            setPaginationEvents({
                limit: paginationEvents.limit,
                offset: paginationEvents.limit * value
            });
        },
        [paginationEvents]
    );

    const handleNewEvent = React.useCallback(() => {
        track('ACTION > CREATE AN EVENT HEADER', {
            'Page Title': 'EVENT LIST'
        });
        history.push(`/ticket/O${organization.id}/events/new`);
    }, [history, organization, track]);

    useTrackingPage('VIEW - EVENT LIST', { 'Page Title': 'EVENT LIST' });

    const showActivationError = React.useMemo(() => {
        return organizationInfos?.can_publish_errors?.includes('account_not_validated');
    }, [organizationInfos]);

    const onSendActivationMail = React.useCallback(() => {
        fetcher
            .post(`/ticket/organizations/${organization.id}/users/${user.profile.sub}/activation_email`, {
                data: { redirect_uri: window.location.href }
            })
            .then(() => {
                toast.success(t('ticket.event.sendMailAccountActivationModal.toast.success'));
            })
            .catch(() => {
                toast.error(t('ticket.event.sendMailAccountActivationModal.toast.error'));
            });
    }, [user, organization]);

    React.useEffect(() => {
        const handleResizeWindow = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResizeWindow);
        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    }, []);
    return (
        <>
            {!loadingEvents && events?.length > 0 && <OnboardingEventList viewType={viewType} />}
            <WeezHeader
                title={t('allEvents')}
                rightComponent={[
                    <Button
                        primary
                        key="new"
                        disabled={loadingEvents}
                        icon={<Icon name={'plus'} margin={false} />}
                        label={t('context_menu.cta_label_orga')}
                        onClick={handleNewEvent}
                    />
                ]}
            >
                {t('allEventsDescription')}
            </WeezHeader>
            {permissions.capacities.has('PyvarCapacity.SWITCH_BO') && organization.feature_flags.includes('EO_NBO_INVITED') && (
                <Message
                    type="default"
                    title={t('ticket.switch-nbo.title')}
                    links={[
                        {
                            onClick: () => {
                                setOpenFeedbacksModal(true);
                                track('ACTION > OPEN CONFIRMATION MODALE', {
                                    'Page Title': 'Event List'
                                });
                            },
                            label: t('ticket.switch-nbo.cta')
                        }
                    ]}
                >
                    <p>{t('ticket.switch-nbo.description')}</p>
                </Message>
            )}
            <WarningMessage organization={organization} />

            {openFeedbacksModal && <FeedBacksModal onClose={() => setOpenFeedbacksModal(false)} open={openFeedbacksModal} />}
            {showActivationError && (
                <Message
                    type="danger"
                    title={t('ticket.event.send_activation_mail.title')}
                    links={[
                        {
                            onClick: onSendActivationMail,
                            label: t('ticket.event.send_activation_mail.cta')
                        }
                    ]}
                >
                    <p>{t('ticket.event.send_activation_mail.description')}</p>
                </Message>
            )}
            <Card>
                <ContentCard className={css['filters-card']}>
                    <FilterSetController
                        className={css['filter-block']}
                        total={countEvents}
                        isLoadingTotal={loadingEvents}
                        labelTotal={countEvents > 1 ? t('ticket.common.events') : t('ticket.common.event')}
                        labelFilters={t('ticket.common.action.filter')}
                        labelReset={t('ticket.common.action.reset')}
                        initialValues={filters}
                        defaultValues={initialFilters}
                        onChange={values => {
                            if (filters.search !== undefined && filters.search.length > 0) {
                                values.search = filters.search;
                            } else {
                                delete values.search;
                            }
                            if (timeStatusFilters.time_status !== undefined) {
                                values.time_status = timeStatusFilters.time_status;
                            }
                            setFilters(values);
                            resetPagination();
                        }}
                        onReset={() => setTimeStatusFilters({ time_status: new Set(['upcoming', 'ongoing', 'over']) })}
                        search={{
                            onChange: (synth, value) => handleSearch(synth, value),
                            handleReset: () => handleSearch(''),
                            placeholder: t('formEvents.search_label'),
                            value: filters['search']
                        }}
                        leftComponent={<SortButton label={t('ticket.common.sort.label')} sortValue={filters.ordering} onClick={handleSort} options={sortOptions} />}
                    >
                        <Filter
                            index="1"
                            multiple
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.accounting.filter.statusLabel')}
                            labelCTA={t('ticket.common.action.apply')}
                            name="publication_status__in"
                            options={options}
                        />
                        {tags && tags.length && (
                            <Filter
                                index="2"
                                multiple
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelName={t('ticket.common.tags')}
                                labelCTA={t('ticket.common.action.apply')}
                                name="tag__in"
                                options={tagOptions}
                            />
                        )}
                    </FilterSetController>

                    <div className={css['filter-boxes']}>
                        {renderFilters}
                        <div className={css['pagination-dropdown']}>
                            <DropdownPageSize
                                labelItem="eventPageSize"
                                current={paginationEvents.limit}
                                onChange={value => handlePaginate(value)}
                                onChangeCurrentpage={setCurrentPage}
                                totalCount={countEvents}
                                loading={loadingEvents}
                                inline
                                pageSizes={[10, 20, 50, 100]}
                            />
                        </div>
                    </div>
                </ContentCard>
            </Card>
            <LoadingCard loading={loadingEvents} height="30vh">
                {events?.length > 0 ? (
                    <>
                        <ActionModals
                            event={eventSelected}
                            events={events}
                            resetPagination={resetPagination}
                            organization={organization}
                            tags={tags}
                            open={actionModalDisplay}
                            onClose={onCloseActionModal}
                        />
                        {events.map((event, index) => (
                            <EventCard
                                key={index}
                                className={index === 0 ? 'onboarding-step-event-card' : null}
                                event={event}
                                resetPagination={resetPagination}
                                organizationInfos={organizationInfos}
                                organization={organization}
                                onChangeActionModal={setActionModalDisplay}
                                onChangeSelected={setEventSelected}
                            />
                        ))}
                        <div className={css['paginator-container']}>
                            <Paginator currentPage={currentPage} onPageChange={handlePage} pageSize={paginationEvents.limit} totalCount={countEvents} />
                        </div>
                    </>
                ) : organizationInfos?.has_event ? (
                    <EmptyCard title={t('noActiveEvents.title')} subTitle={<Trans i18nKey="noActiveEvents.subtitle" />} />
                ) : (
                    <Card>
                        <Card.Content>
                            <EmptyState icon={<Icon name="tickets" />} title={t('noEventsYet')} subtitle={t('ticket.common.searchTotal')} />
                        </Card.Content>
                    </Card>
                )}
            </LoadingCard>
        </>
    );
}

EventSelection.propTypes = {
    organization: PropTypes.object.isRequired
};

export default withTranslation()(EventSelection);
